import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import useProductDetail from "../Hooks/useProductDetail";
import ImageMagnifier from "../components/ImageMagnifier";
import ReviewForm from "../components/ReviewForm";
import SizeChartPopup from "../components/SizeChartPopup";
import { useDispatch } from "react-redux";
import { addToCart } from "../redux/actions/cartActions";
import { Checkbox } from "antd";
import SelectableAttributes from "../components/SelectableAttributes";
import Modal from "./../components/Modal";
import { ToastContainer, toast } from "react-toastify";

const ProductDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { product, loading, error } = useProductDetail(id);
  const [selectedSize, setSelectedSize] = useState(); // Default size
  const [showChart, setShowChart] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
  };
  const handleNotification = () => {
    setIsModalOpen(true);
  };
  const handlePriceChange = (price) => {
    setTotalPrice(price);
  };

  const increaseQuantity = () => setQuantity((prev) => prev + 1);
  const decreaseQuantity = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const openFullScreen = (image) => {
    setSelectedImage(image);
    setIsFullScreen(true);
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
    setSelectedImage(null);
  };
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeFullScreen();
    }
  };
  useEffect(() => {
    window.scroll(0, 0);
  }, []);
  if (loading) {
    return (
      <div className="mx-auto md:px-8 w-full min-h-full">
        {/* Loading placeholder */}
        <div className="grid grid-cols-12 gap-8 w-full animate-pulse">
          <div className="md:col-span-7 col-span-12 grid grid-cols-12 gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="col-span-6 bg-gray-300 h-[342px] md:h-[442px] lg:h-[542px] rounded-md"
              />
            ))}
          </div>
          <div className="md:col-span-5 col-span-12 space-y-4">
            <div className="h-8 bg-gray-300 w-1/2 rounded-md" />
            <div className="h-6 bg-gray-300 w-1/3 rounded-md" />
            <div className="h-4 bg-gray-300 w-1/4 rounded-md" />
            <div className="h-10 bg-gray-300 w-1/2 rounded-md mt-6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;

  // Grouping attributes by attribute name
  const groupedAttributes = product.attributes.reduce((acc, attr) => {
    attr.attribute_values.forEach(
      ({ attribute_name, value, is_Selectable }) => {
        if (!is_Selectable) {
          if (!acc[attribute_name]) {
            acc[attribute_name] = [];
          }
          acc[attribute_name].push(value);
        }
      }
    );
    return acc;
  }, {});
  const selectAttributes = product.attributes.reduce((acc, attr) => {
    attr.attribute_values.forEach(
      ({ attribute_name, value, is_Selectable }) => {
        if (is_Selectable) {
          if (!acc[attribute_name]) {
            acc[attribute_name] = [];
          }
          acc[attribute_name].push(value);
        }
      }
    );
    return acc;
  }, {});

  const handleAddToCart = () => {
    if (!selectedSize && product.sizes?.length > 0) {
      toast.warn("Please select a size before adding to the cart.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }

    const productDetails = {
      id: product.id,
      image: product.images[0].image, // Assuming the first image
      name: product.name,
      size: selectedSize,
      price: totalPrice > 0 ? totalPrice : product.price,
      quantity: quantity,
    };

    dispatch(addToCart(productDetails));
  };
  return (
    <>
      {product.images && product.images.length > 0 && (
        <Modal
          isOpen={isModalOpen}
          closeModal={closeModal}
          heading="Product Notification"
          data={{ id: id, image: product.images[0].image }}
          dataMode="productNotification"
        />
      )}
      <div className="mx-auto md:px-8 w-full">
        {/* Full-Screen Modal */}
        {isFullScreen && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50"
            onClick={handleOverlayClick}
          >
            <div className="relative">
              <ImageMagnifier
                src={selectedImage}
                width={"100%"}
                height={"100%"}
                magnifierHeight={300}
                magnifierWidth={200}
                zoomLevel={2}
                alt="Sample Image"
              />
            </div>
            <button
              onClick={closeFullScreen}
              className="absolute top-4 right-4 text-white text-4xl"
            >
              &times;
            </button>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="breadcrumb flex items-center md:px-5 p-3 text-gray-500">
          <Link to="/" className="hover:underline text-sm">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-xs mx-2"></i>
          <p className="hover:underline text-sm text-black font-medium">
            {product.name}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-8 w-full mb-12">
          {/* Images */}
          <div className="md:col-span-7 col-span-12 md:ps-5 px-3 grid grid-cols-12 gap-4 overflow-y-auto no-scrollbar h-screen">
            {product.images.map((image, index) => (
              <div
                key={index}
                className="col-span-6 product-image-container relative overflow-hidden h-[342px] md:h-[442px] lg:h-[542px] cursor-pointer"
                onClick={() => openFullScreen(image.image)}
              >
                <ImageMagnifier
                  src={image.image}
                  width={"100%"}
                  height={"100%"}
                  magnifierHeight={300}
                  magnifierWidth={200}
                  zoomLevel={2}
                  alt="Sample Image"
                />
              </div>
            ))}
          </div>

          {/* Product Details */}
          <div className="md:col-span-5 col-span-12 md:p-0 md:pe-5 pe-3 p-3 w-full">
            <h2 className="text-lg font-bold uppercase mb-2">{product.name}</h2>

            <p className="text-xm leading-4 font-medium text-gray-800 mb-2">
              {totalPrice > 0 ? (
                <>
                  <span className="text-red-500 line-through mr-2">
                    Rs: {product.price}
                  </span>
                  <span className="text-gray-800">Rs: {totalPrice}</span>{" "}
                  {/* Show the new price */}
                </>
              ) : (
                <span>Rs: {product.price}</span>
              )}
            </p>
            <p className="text-xs text-gray-800 mb-4">SKU: {product.heading}</p>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-md font-semibold mr-4">
                  SIZE: {selectedSize}
                </span>
                {product?.attributes[0]?.attribute_values?.map((item) => (
                  <>
                  {item.attribute_name ==='Size'?<button
                    key={item.id} 
                    onClick={() => setSelectedSize(item.value)}
                    className={`p-1 me-2 border text-xs bg-white text-black ${
                      selectedSize === item.value ? "border-2 border-black" : ""
                    }`}
                  >
                    {item.value}
                  </button>:""}
                  </>
                ))}
              </div>

              {/* Size Chart Button */}
              <div>
                <button
                  onClick={() => setShowChart(true)}
                  className="flex text-sm items-center gap-2 text-gray-600 hover:text-black"
                >
                  {product.sizes.length > 0 ? (
                    <>
                      <i class="fa-solid fa-ruler"></i>
                      <span className="underline">Size Chart</span>
                    </>
                  ) : (
                    ""
                  )}
                </button>
              </div>
            </div>
            {/* Quantity and Add to Bag */}
            <div className="flex items-center mb-6">
              {product.stock > 0 ? (
                <>
                  <button onClick={decreaseQuantity} className="text-2xl">
                    <i className="fa-solid fa-circle-minus"></i>
                  </button>
                  <span className="px-4">{quantity}</span>
                  <button onClick={increaseQuantity} className="text-2xl">
                    <i className="fa-solid fa-circle-plus"></i>
                  </button>
                </>
              ) : (
                ""
              )}

              {product.stock > 0 && (
                <button
                  onClick={handleAddToCart}
                  className="bg-black text-white py-2 px-6 ml-4 rounded-xl font-semibold"
                >
                  ADD TO BAG
                </button>
              )}

              {product.stock < 1 && (
                <div className="flex justify-end col-span-4">
                  <button
                    onClick={handleNotification}
                    className="bg-black text-white py-2 px-6 ml-4 rounded-xl font-semibold"
                  >
                    <span className="button-text">Notify</span>
                    <i className="fa fa-bell hidden-icon ml-2"></i>
                  </button>
                </div>
              )}
              {product.stock < 1 && (
                <div
                  style={{
                    color: "Red",
                    fontWeight: "bold",
                    marginTop: "5px",
                    marginLeft: "5px",
                    border: "1px solid red",
                    backgroundColor: "#fff",
                    padding: "10px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "90px",
                    height: "90px",
                    textAlign: "center",
                  }}
                >
                  <span>Out of Stock</span>
                </div>
              )}
            </div>
            {/* Product Details Accordion */}
            <div>
              <div
                className="flex items-center cursor-pointer"
                onClick={() => setShowProductDetails(!showProductDetails)}
              >
                <h3 className="text-xm font-semibold me-4">PRODUCT DETAILS</h3>
                <i
                  className={`fa-solid ${
                    showProductDetails ? "fa-minus" : "fa-plus"
                  }`}
                ></i>
              </div>
              {showProductDetails && (
                <div className="text-gray-700 mb-4 transition-all">
                  <p className="mb-2 text-xs">{product.description}</p>

                  {/* Displaying grouped attributes */}
                  <div className="text-xs">
                    {Object.entries(groupedAttributes).map(
                      ([attrName, values]) => (
                        <div key={attrName} className="mb-2">
                          <strong>{attrName}:</strong> {values.join(", ")}
                        </div>
                      )
                    )}
                  </div>
                  <div className="mt-3">
                    {" "}
                    <SelectableAttributes
                      attributes={selectAttributes}
                      onPriceChange={handlePriceChange}
                    />{" "}
                  </div>
                </div>
              )}
            </div>

            {/* Review Section */}
            <div className="mt-8">
              <div
                className="flex items-center cursor-pointer"
                onClick={() => setShowReview(!showReview)}
              >
                <h3 className="text-xm font-semibold me-4">LEAVE A REVIEW</h3>
                <i
                  className={`fa-solid ${showReview ? "fa-minus" : "fa-plus"}`}
                ></i>
              </div>
              <div className="flex justify-center">
                <button
                  className="bg-black text-white my-4 py-2 px-6 ml-4 md:text-lg text-xs uppercase font-semibold"
                  onClick={() => setShowReview(!showReview)}
                >
                  {showReview ? "Cancel Review" : "Write A Review"}
                </button>
              </div>
              {showReview && (
                <ReviewForm
                  onCancel={() => setShowReview(false)}
                  id={product.id}
                />
              )}
            </div>

            <div className="text-xs text-black transition-all">
              <p>
                <strong>Note:</strong> Actual product color may vary slightly
                from the image.
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-6 justify-center mt-8">
              <button className="md:px-5 md:py-2 px-3 py-1 md:text-lg text-xm border border-black rounded-md">
                Previous
              </button>
              <button className="md:px-5 md:py-2 px-3 py-1 md:text-lg text-xm border border-black rounded-md">
                Next
              </button>
            </div>
          </div>
        </div>
        <SizeChartPopup
          productName={product.name}
          sizes={product.sizes}
          show={showChart}
          onClose={() => setShowChart(false)}
        />
      </div>
      <ToastContainer />
    </>
  );
};

export default ProductDetail;
